'use client';

import { useCallback } from 'react';

import { useUrlQueryState } from './useUrlQueryState';

/** The query key the page tabs live in, the same one `ControlledTabs` reads on mount. */
export const ACTIVE_TAB_PARAM = 'activeTab';

interface IUseActiveTabParamResult {
  /** The tab the address bar asks for, or the fallback when it asks for nothing valid. */
  activeTab: string;
  /** Writes the tab back, so the link in the bar always matches what is on screen. */
  onTabChange: (tab: string) => void;
}

/**
 * The active tab READ from the address bar on every render, not only on the first one.
 *
 * `ControlledTabs` resolves the `activeTab` query inside its `useState` initialiser, so a
 * link that only changes the query of the page ALREADY on screen moved the address bar and
 * left the tab where it was. That is what made the roadmap's "Resolver" look broken: from
 * Configurações › Implantação, `/settings?activeTab=product` changed the URL and kept the
 * person on Implantação. Owning the value here makes the tab follow the link, and keeps
 * the link shareable because every switch writes the query back.
 */
export function useActiveTabParam(tabs: ReadonlyArray<string>, fallback: string): IUseActiveTabParamResult {
  const { getParam, setParams } = useUrlQueryState();
  const pedida = getParam(ACTIVE_TAB_PARAM);
  // A hand typed tab that does not exist falls back instead of rendering no panel at all.
  const activeTab = tabs.includes(pedida) ? pedida : fallback;

  const onTabChange = useCallback(
    (tab: string): void => {
      setParams({ [ACTIVE_TAB_PARAM]: tab });
    },
    [setParams],
  );

  return { activeTab, onTabChange };
}
